import { getRecipesByMeal } from '../services/recipeService';
import { MealSlot, RecipeData, GroceryItem } from '../types/meals';

/**
 * Fetches a random recipe from a specific meal category
 */
export const getRandomRecipeFromCategory = async (
  mealType: string, 
  useOwnData: boolean
): Promise<RecipeData | null> => {
  try {
    const recipes = await getRecipesByMeal(
      mealType.toLowerCase() as 'breakfast' | 'lunch' | 'snack' | 'dinner', 
      useOwnData
    );
    if (recipes.length === 0) {
      console.warn(`No recipes found for ${mealType}`);
      return null;
    }
    const randomIndex = Math.floor(Math.random() * recipes.length);
    return recipes[randomIndex];
  } catch (error) {
    console.error('Error getting random recipe:', error);
    return null;
  }
};

/**
 * Generates a grocery list from selected meal slots
 */
export const generateGroceryList = (mealSlots: MealSlot[]): GroceryItem[] => {
  const groceryMap = new Map<string, GroceryItem>();
  
  mealSlots.forEach((slot) => {
    if (!slot.recipe || !slot.recipe.ingredients) return;
    
    const recipe = slot.recipe;
    
    recipe.ingredients.forEach((ingredient) => {
      const key = ingredient.name.toLowerCase();
      const quantity = parseFloat(ingredient.quantity) || 0;
      
      if (groceryMap.has(key)) {
        const existing = groceryMap.get(key)!;
        if (existing.unit === ingredient.unit) {
          existing.totalQuantity += quantity;
          existing.meals.push(recipe.title);
        } else {
          // Different units - create separate entry
          const newKey = `${key}_${ingredient.unit}`;
          groceryMap.set(newKey, {
            name: ingredient.name,
            totalQuantity: quantity,
            unit: ingredient.unit,
            meals: [recipe.title]
          });
        }
      } else {
        groceryMap.set(key, {
          name: ingredient.name,
          totalQuantity: quantity,
          unit: ingredient.unit,
          meals: [recipe.title]
        });
      }
    });
  });
  
  return Array.from(groceryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Copies grocery list to clipboard
 */
export const copyGroceryListToClipboard = async (groceryItems: GroceryItem[]): Promise<boolean> => {
  if (groceryItems.length === 0) return false;

  const groceryText = groceryItems
    .map(item => `• ${item.totalQuantity} ${item.unit} ${item.name}`)
    .join('\n');

  const fullText = `🛒 Grocery List\n\n${groceryText}`;

  try {
    await navigator.clipboard.writeText(fullText);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

/**
 * Calculates total calories from meal slots
 */
export const calculateTotalCalories = (mealSlots: MealSlot[]): number => {
  return mealSlots.reduce((sum: number, slot: MealSlot) => sum + (slot.recipe?.calories || 0), 0);
};

/**
 * Calculates total protein from meal slots
 */
export const calculateTotalProtein = (mealSlots: MealSlot[]): number => {
  return mealSlots.reduce((sum: number, slot: MealSlot) => sum + (slot.recipe?.protein || 0), 0);
};

/**
 * Gets available recipes for a meal type with optional search filtering
 */
export const getAvailableRecipes = async (mealType: string, useOwnData: boolean): Promise<RecipeData[]> => {
  try {
    return await getRecipesByMeal(mealType.toLowerCase() as 'breakfast' | 'lunch' | 'snack' | 'dinner', useOwnData);
  } catch (error) {
    console.error('Error getting available recipes:', error);
    return [];
  }
};

/**
 * Filters recipes based on search term
 */
export const getFilteredRecipes = async (mealType: string, searchTerm: string, useOwnData: boolean): Promise<RecipeData[]> => {
  try {
    const allRecipes = await getAvailableRecipes(mealType, useOwnData);
    if (!searchTerm) return allRecipes;
    
    return allRecipes.filter(recipe =>
      recipe.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  } catch (error) {
    console.error('Error filtering recipes:', error);
    return [];
  }
};